import React, { useState, FC } from 'react'
import {
  CarouselItem,
  UnstyledCarouselScrollArea,
  CarouselRoot,
  CarouselState,
  Image,
  CarouselControls,
  CarouselPreviousButton,
  CarouselNextButton,
  Box,
  ScreenReaderOnly,
} from '@moonpig/launchpad-components'
import styled, { css } from 'styled-components'
import { system as s } from '@moonpig/launchpad-system'
import { ThemeInterface, colorValue } from '@moonpig/launchpad-theme'
import { breakpoint } from '@moonpig/launchpad-utils'
import { useGiftImageCarouselLocaleText } from './GiftImageCarousel.locale'
import { FavouriteButton } from '../FavouriteButton'
import { ProductImage } from '../../types'
import { useProductsLocaleText } from '../../locale'

const StyledMainImageContainer = styled(Box)`
  ${s({
    position: 'relative',
    mb: { xs: 4, lg: 5 },
    borderRadius: 3,
    overflow: 'hidden',
    bgcolor: 'colorBackground01',
  })}
  &::before {
    content: '';
    display: block;
    padding-top: 100%;
  }
`

const StyledMainBox = styled(Box)`
  ${s({ bgcolor: 'colorBackground01' })}
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  transform: translateY(-50%);
  background: #fff;
  line-height: 0;
`

const StyledMainImage = styled(Image)`
  width: 100%;
  background: ${colorValue('colorBackground01')};
`

const StyledButton = styled.button<{ active: boolean; theme: ThemeInterface }>`
  ${s({
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    width: { xs: '5rem', md: '6rem', lg: '7.35rem' },
    bgcolor: 'colorBackground01',
    borderRadius: 2,
    overflow: 'hidden',
  })}

  &::after {
    ${({ active }) =>
      active &&
      css`
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        ${s({ border: 2, borderColor: 'colorPrimary02', borderRadius: 2 })}

        ${breakpoint('lg')} {
          ${s({ border: 3, borderColor: 'colorPrimary02', borderRadius: 2 })}
        }
      `}
  }
`

const StyledCarouselRoot = styled(CarouselRoot)`
  ${s({
    px: { xs: 5, md: 6 },
  })}
`

const StyledCarouselScrollArea = styled(UnstyledCarouselScrollArea)`
  ${({ theme }) =>
    s({
      gap: { xs: `${theme.spacing[4]}px`, lg: `${theme.spacing[5]}px` },
      py: 2,
    })}

  padding-top: 2px;
`

const StyledFavouriteButton = styled(FavouriteButton)`
  position: absolute;
  top: 8px;
  right: 8px;
`

const Carousel: FC<React.PropsWithChildren<{ title: string }>> = ({
  title,
  children,
}) => {
  const localiseText = useGiftImageCarouselLocaleText()
  return (
    <Box>
      <CarouselState>
        <Box marginX={{ xs: -5, md: -6 }}>
          <StyledCarouselRoot
            title={localiseText('common.gift_image_carousel.root_title_text', {
              title,
            })}
          >
            <StyledCarouselScrollArea>{children}</StyledCarouselScrollArea>
            <CarouselControls>
              <CarouselPreviousButton />
              <CarouselNextButton />
            </CarouselControls>
          </StyledCarouselRoot>
        </Box>
      </CarouselState>
    </Box>
  )
}

const mapProductImagesToCarouselImages = (
  productImages: ProductImage[],
): Array<GiftImageCarouselItem> => {
  return productImages.map(image => ({
    small: image.small.jpgUrl,
    medium: image.medium.jpgUrl,
    large: image.large.jpgUrl,
    extraLarge: image.extraLarge.jpgUrl,
  }))
}

const mapImagesToCarouselImages = (
  images: string[],
): Array<GiftImageCarouselItem> => {
  return images.map(src => ({
    small: src,
    medium: src,
    large: src,
    extraLarge: src,
  }))
}

type ImageSizeKey = 'small' | 'medium' | 'large' | 'extraLarge'
type GiftImageCarouselItem = Record<ImageSizeKey, string>

export const GiftImageCarousel: FC<{
  images: string[]
  title: string
  id?: string
  productImages?: ProductImage[]
  isFavourited: boolean
  onFavouriteToggle: () => void
}> = ({
  images,
  title,
  id,
  productImages,
  isFavourited,
  onFavouriteToggle,
}) => {
  const [selectedItemIndex, setSelectedItemIndex] = useState(0)
  const localeText = useProductsLocaleText()
  const carouselItems: Array<GiftImageCarouselItem> = productImages
    ? mapProductImagesToCarouselImages(productImages)
    : mapImagesToCarouselImages(images)

  const handleSelectCarouselItem = (index: number) => {
    setSelectedItemIndex(index)
  }

  const selectedItem = carouselItems[selectedItemIndex]

  return (
    <Box
      data-testid="mp-find-gift-image-carousel"
      data-product-id={id}
      maxWidth="30.25rem"
      margin="0 auto"
    >
      {carouselItems.length && (
        <StyledMainImageContainer>
          <StyledMainBox>
            <StyledFavouriteButton
              title={title}
              isFavourited={isFavourited}
              onFavouriteToggle={onFavouriteToggle}
            />
            <StyledMainImage
              src={selectedItem.large}
              zoomSrc={selectedItem.large}
              alt={title}
              width="100%"
            />
          </StyledMainBox>
        </StyledMainImageContainer>
      )}
      {carouselItems.length > 1 ? (
        <Carousel title={title}>
          {carouselItems.map((carouselItem, index) => (
            <CarouselItem
              // eslint-disable-next-line react/no-array-index-key
              key={`${carouselItem.small}-${index}`}
              onClick={() => handleSelectCarouselItem(index)}
              tabIndex={undefined}
            >
              <Box position="relative" height="100%">
                <StyledButton
                  className="focus-visible"
                  active={index === selectedItemIndex}
                >
                  <Image width="100%" src={carouselItem.small} aria-hidden />
                </StyledButton>
                <ScreenReaderOnly>
                  {localeText('product_image.image_label', title, index + 1)}
                </ScreenReaderOnly>
              </Box>
            </CarouselItem>
          ))}
        </Carousel>
      ) : null}
    </Box>
  )
}
